import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";
import { nav, site } from "@/data/content";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled || open
            ? "bg-paper/85 backdrop-blur-md border-b border-ink/10"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link
            to="/"
            className="font-serif text-xl lg:text-2xl tracking-[0.18em] uppercase"
            data-testid="navbar-brand-link"
          >
            Luisa Miceli
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `link-underline text-[13px] tracking-wide transition-colors ${
                    isActive ? "text-bordeaux" : "text-ink hover:text-bordeaux"
                  }`
                }
                data-testid={`nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            data-testid="menu-toggle-button"
          >
            {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-paper lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="h-full flex flex-col justify-center px-8 pt-20">
              {nav.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="border-b border-ink/10"
                >
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `block py-5 font-serif text-4xl ${isActive ? "italic text-bordeaux" : "text-ink"}`
                    }
                    data-testid={`mobile-nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-10 flex items-center justify-between"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted3">
                  Pensiero · Parole · Libertà
                </p>
                <a
                  href={site.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram di Luisa Miceli"
                  className="text-ink hover:text-bordeaux transition-colors"
                  data-testid="mobile-instagram-link"
                >
                  <Instagram size={22} strokeWidth={1.5} />
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
